// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { Cursors } from '@shared/commons/Cursors';
import { DataCacheToolFactory } from '@shared/commons/cache/DataCacheToolFactory';
import { ToolsCache } from '@shared/commons/cache/ToolsCache';
import { KeyCode, KeyEventProcessor } from '@shared/commons/KeyEventProcessor';
import { MouseButtons } from '@front/controls/UtilsClasses/ControlsUtils';
import { PortfolioChartReturnsRenderer } from '../Portfolio/Chart/PortfolioChartReturnsRenderer';
import { TerceraChartIndicatorRenderer } from './Renderers/Indicators/TerceraChartIndicatorRenderer';
import { TerceraChartIndicatorStorageRenderer } from './Renderers/Indicators/TerceraChartIndicatorStorageRenderer';
import { TerceraChartPriceScaleRenderer, TerceraChartPriceScaleRendererAligment, TerceraChartPriceScaleRendererSettings, TerceraChartPriceScaleRendererWindowType } from './Renderers/Scales/TerceraChartPriceScaleRenderer';
import { TerceraChartTimeScaleRenderer } from './Renderers/Scales/TerceraChartTimeScaleRenderer';
import { TerceraChartCrossHairRenderer } from './Renderers/TerceraChartCrossHairRenderer';
import { TerceraChartDayHighLowRenderer } from './Renderers/TerceraChartDayHighLowRenderer';
import { TerceraChartHighlightExtendedSessionRenderer } from './Renderers/TerceraChartHighlightExtendedSessionRenderer';
import { TerceraChartInfoWindowRenderer } from './Renderers/TerceraChartInfoWindowRenderer';
import { TerceraChartMainPriceRenderer } from './Renderers/TerceraChartMainPriceRenderer';
import { TerceraChartNewAlertRenderer } from './Renderers/TerceraChartNewAlertRenderer';
import { TerceraChartOverlayStorageRenderer } from './Renderers/TerceraChartOverlayStorageRenderer';
import { TerceraChartPlateRenderer } from './Renderers/TerceraChartPlateRenderer';
import { TerceraChartScalingByRectangleRenderer } from './Renderers/TerceraChartScalingByRectangleRenderer';
import { TerceraChartSpreadRenderer } from './Renderers/TerceraChartSpreadRenderer';
import { TerceraChartTimeToNextBarRenderer } from './Renderers/TerceraChartTimeToNextBarRenderer';
import { TerceraChartVolumeBarsRenderer } from './Renderers/TerceraChartVolumeBarsRenderer';
import { TerceraChartWatermarkRenderer } from './Renderers/TerceraChartWatermarkRenderer';
import { TerceraChartEraserRenderer } from './Renderers/Tools/TerceraChartEraserRenderer';
import { TerceraChartNewToolRenderer } from './Renderers/Tools/TerceraChartNewToolRenderer';
import { TerceraChartSelectionRenderer } from './Renderers/Tools/TerceraChartSelectionRenderer';
import { TerceraChartToolInstanceRenderer } from './Renderers/Tools/TerceraChartToolInstanceRenderer';
import { TerceraChartToolRenderer } from './Renderers/Tools/TerceraChartToolRenderer';
import { TerceraChartTradingToolsRenderer } from './Renderers/Tools/TerceraChartTradingToolsRenderer';
import { TradingCentralLinesRenderer } from './Renderers/TradingCentralLinesRenderer';
import { TerceraChartCashItemSeries } from './Series/TerceraChartCashItemSeries';
import { TerceraChartCashItemSeriesSettings } from './Series/TerceraChartCashItemSeriesSettings';
import { TerceraChartOverlaySeries } from './Series/TerceraChartOverlaySeries';
import { TerceraChartAction, TerceraChartActionEnum, TerceraChartToolbarsEnum, CancelActionContext } from './TerceraChartAction';
import { TerceraChartActionProcessor } from './TerceraChartActionProcessor/TerceraChartActionProcessor';
import { ModelDataType, ChartLoadTrigger, TerceraChartMVCController, TerceraChartMVCModel } from './TerceraChartMVC';
import { TerceraChartOverlay } from './TerceraChartOverlay';
import { ChartHistoryType, TerceraChartDrawingType } from './Utils/ChartConstants';
import { TerceraChartWindow } from './Windows/TerceraChartWindow';
import { NewToolClickStrategy } from './Tools/NewToolClickStrategy';
import { TerceraChartWindowsContainer } from './TerceraChartWindowsContainer';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { HistorySortOrder, ReloadHistoryParams } from '@shared/utils/History/ReloadHistoryParams';
import { Periods, TradeAnalysisType } from '@shared/utils/History/TFInfo';
import { CashItemPointsCoverter } from './Utils/PointsConverter/CashItemPointsConverter';
import { ArrayUtils } from '@shared/utils/ArrayUtils';
import { DynProperty } from '@shared/commons/DynProperty';
import { IndicatorManager } from '@shared/commons/cache/indicators/IndicatorManager';
import { DataCache } from '@shared/commons/DataCache';
import { TerceraChartAutoLoadHistoryManager } from './TerceraChartAutoLoadHistoryManager';
import { CashItem } from '@shared/commons/cache/History/CashItem';
import { DataCacheToolType } from '@shared/commons/cache/DataCacheToolEnums';
import { TerceraChartBase } from './TerceraChartBase';
import { TerceraChartMouseState } from './Utils/TerceraChartMouseState';
import { TerceraChartPriceScaleLayoutInfo } from './Utils/TerceraChartPriceScaleLayoutInfo';
import { TerceraChartScrollerRenderer } from './Renderers/TerceraChartScrollerRenderer';
import { TerceraChartZoomRenderer, ChartZoomRendererButtons } from './Renderers/TerceraChartZoomRenderer';
import { NOT_FOUND_INTERVAL_VALUE } from '@shared/commons/cache/History/HistoryConstants';
import { TerceraChartTimeScaleRendererSettings } from './Renderers/Scales/TerceraChartTimeScaleRendererSettings';
import { SearchHelper } from '@shared/commons/SearchHelper';
import { InfoWindowDisplayMode } from './Renderers/InfoWindow/InfoWindowDisplayMode';
import { getTerceraChartCashItemSeriesDataTypeBasisTypeKey, getTerceraChartCashItemSeriesDataTypeKey, TerceraChartCashItemSeriesDataType, TerceraChartCashItemSeriesDataTypeBasisType } from './Series/TerceraChartCashItemSeriesEnums';
import { GoToTabletRenderer } from './Renderers/GoToTabletRenderer';
import { TimeSpan } from '@shared/utils/Time/TimeSpan';
import { TerceraChartTimeScaleSeparatorsRenderer } from './Renderers/TerceraChartTimeScaleSeparatorsRenderer';
import { LayersEnum } from './Utils/LayersEnum';
import { createContextMenuItemsFromEnum, NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
import { TerceraChartHistoryType } from './TerceraChartUtils';
import { ITerceraChartShowScreenType } from './ITerceraChartShowScreenType';
import { PanelNames } from '@front/controls/UtilsClasses/FactoryConstants';
//
// Чарт, аналог чарт на клиенте
//
export class TerceraChart extends TerceraChartBase {
    constructor(ctx, terceraChartPanelContext, chartId) {
        super(ctx, terceraChartPanelContext);
        this.LoadingNow = true;
        this.mainPriceRenderer = null;
        this.volumeBarsRenderer = null;
        this.OnLoadingStart = new CustomEvent();
        this.OnLoadingFinish = new CustomEvent();
        this.SelectionRenderer = null;
        this.EraserRenderer = null;
        this.DayHighLowRenderer = null;
        this.SpreadRenderer = null;
        this.PortfolioChartReturnsRenderer = null;
        this.TerceraChartTimeToNextBarRenderer = null;
        this.TerceraChartCrossHairRenderer = null;
        this.overlayStorageRenderer = null;
        this.Overlays = [];
        this.activeOverlay = null;
        this.FocusedOverlay = null;
        this.PortfolioAssetReturnVisible = false;
        this.autoLoadingStateChanged = new CustomEvent();
        this.loadHistoryAbortController = null;
        this.loadOverlayHistoryAbortController = null;
        this.isMaxHistoryLoaded = false;
        this.OnAddIndicator = new CustomEvent();
        this.OnRemoveIndicator = new CustomEvent();
        this.OnAddOverlay = new CustomEvent();
        this.OnRemoveOverlay = new CustomEvent();
        this.minRealBarsCount = 2;
        this._historyCoef = 2;
        this.ChartID = chartId;
        this.LoadingNow = true;
        this.model = new TerceraChartMVCModel(DataCache);
        this.model.ModelItemChanged.Subscribe(this.MVCModelItemChanged, this);
        this.model.refreshChart.Subscribe(this.RefreshChart, this);
        this.chartController = new TerceraChartMVCController(this);
        this.chartController.SuspendRefreshChart = true; // UnSuspendAfter populate
        this.cashItemSeriesSettings = new TerceraChartCashItemSeriesSettings(this.model);
        this.yScaleRendererSettings = this.GetChartPriceScaleRendererSettings();
        this.TerceraChartActionProcessor = new TerceraChartActionProcessor(this);
        this.xScaleRendererSettings = this.GetChartTimeScaleRendererSettings();
        this.NewToolStrategy = new NewToolClickStrategy(this.CreateTool.bind(this), this);
        ToolsCache.OnChangeTool.Subscribe(this.ProcessChangeTool, this);
        this.autoLoadManager = new TerceraChartAutoLoadHistoryManager(this);
        this.autoLoadManager.loadingStateChanged.Subscribe(this._autoLoadingStateChanged, this);
        this.TerceraChartActionProcessor.OnActionProcessor.Subscribe(this.OnActionProcessor, this);
        this.setAutoLoadHistory(true);
    }
    getType() {
        return 'TerceraChart';
    }
    get rightBarIndexIncludingEmptyBars() {
        return this._rightBarIndexIncludingEmptyBars;
    }
    set rightBarIndexIncludingEmptyBars(value) {
        this.mainWindow.i1 = value + this.MainCashItemSeries.leftPaddingBarsCount; // add leftPaddingBarsCount because empty bars are added to the left at the beginning, shifting the previous bars and their indices
        this._rightBarIndexIncludingEmptyBars = value;
    }
    get historyCoef() {
        return this._historyCoef;
    }
    set historyCoef(value) {
        this._historyCoef = value;
    }
    // #region Properties/Callback
    Properties() {
        let properties = super.Properties();
        const mainWindow = this.mainWindow;
        // #region Scale type
        let SeparatorGroup = '#0#' + Resources.getResource('property.SeparatorGroup.ScaleType');
        let prop = null;
        const timeFrameInfo = this.TimeFrameInfo();
        if (!isNullOrUndefined(timeFrameInfo)) {
            const tradeAnalysisType = timeFrameInfo.TradeAnalysisType();
            if (!isNullOrUndefined(tradeAnalysisType) &&
                tradeAnalysisType !== TradeAnalysisType.Cluster &&
                tradeAnalysisType !== TradeAnalysisType.MarketProfile) {
                prop = new DynProperty('relativeScaleType', this.cashItemSeriesSettings.DataType, DynProperty.COMBOBOX, DynProperty.PRICE_SCALE_GROUP);
                prop.sortIndex = 1;
                prop.objectVariants = createContextMenuItemsFromEnum(TerceraChartCashItemSeriesDataType, getTerceraChartCashItemSeriesDataTypeKey);
                prop.assignedProperty = ['relativeCalcType', 'logBase'];
                prop.separatorGroup = SeparatorGroup;
                properties.push(prop);
            }
        }
        prop = new DynProperty('relativeCalcType', this.cashItemSeriesSettings.BasisType, DynProperty.COMBOBOX, DynProperty.PRICE_SCALE_GROUP);
        prop.sortIndex = 2;
        prop.enabilityValue = [TerceraChartCashItemSeriesDataType.Relative, TerceraChartCashItemSeriesDataType.Log];
        prop.enabled =
            this.cashItemSeriesSettings.DataType === TerceraChartCashItemSeriesDataType.Relative ||
                this.cashItemSeriesSettings.DataType === TerceraChartCashItemSeriesDataType.Log;
        const relativeCalcTypeAddFilter = (value) => {
            return (value === TerceraChartCashItemSeriesDataTypeBasisType.BeginOfDay ||
                value === TerceraChartCashItemSeriesDataTypeBasisType.BeginOfScreen);
        };
        prop.objectVariants = createContextMenuItemsFromEnum(TerceraChartCashItemSeriesDataTypeBasisType, getTerceraChartCashItemSeriesDataTypeBasisTypeKey, relativeCalcTypeAddFilter);
        prop.separatorGroup = SeparatorGroup;
        properties.push(prop);
        prop = new DynProperty('logBase', this.cashItemSeriesSettings.logDataConverter.LogBase, DynProperty.DOUBLE, DynProperty.PRICE_SCALE_GROUP);
        prop.sortIndex = 3;
        prop.enabilityValue = [TerceraChartCashItemSeriesDataType.Log];
        prop.enabled = this.cashItemSeriesSettings.DataType === TerceraChartCashItemSeriesDataType.Log;
        prop.minimalValue = 1.01;
        prop.maximalValue = 100;
        prop.increment = 0.01;
        prop.decimalPlaces = 2;
        prop.separatorGroup = SeparatorGroup;
        properties.push(prop);
        // #endregion
        // Zoom
        SeparatorGroup = '#1#' + Resources.getResource('property.SeparatorGroup.Zoom');
        prop = new DynProperty('autoYScale', mainWindow.AutoScale, DynProperty.RADIOBUTTON_COMBOITEM, DynProperty.PRICE_SCALE_GROUP);
        prop.assignedProperty = ['FitIndicator', 'FitOrders', 'FitHighLow', 'FitDrawings', 'FitAlerts'];
        prop.objectVariants = [
            { text: Resources.getResource('property.autoYScale.Auto'), value: true },
            { text: Resources.getResource('property.autoYScale.Manual'), value: false }
        ];
        prop.COMBOBOX_TYPE = DynProperty.INTEGER;
        prop.separatorGroup = SeparatorGroup;
        prop.sortIndex = 0;
        properties.push(prop);
        // xscale
        prop = new DynProperty('xScale', mainWindow.XScale, DynProperty.DOUBLE, DynProperty.HIDDEN_GROUP);
        properties.push(prop);
        if (this.TerceraChartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.FitIndicators)).Visible) {
            prop = new DynProperty('FitIndicator', mainWindow.indicatorRendererSettings.UseInAutoscale, DynProperty.BOOLEAN, DynProperty.PRICE_SCALE_GROUP);
            prop.enabilityValue = true;
            prop.enabled = mainWindow.AutoScale;
            prop.sortIndex = 1;
            prop.separatorGroup = SeparatorGroup;
            properties.push(prop);
        }
        if (this.TerceraChartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.FitOrders)).Visible) {
            prop = new DynProperty('FitOrders', this.TerceraChartTradingToolsRenderer.OrderScale, DynProperty.BOOLEAN, DynProperty.PRICE_SCALE_GROUP);
            prop.enabilityValue = true;
            prop.enabled = mainWindow.AutoScale;
            prop.sortIndex = 2;
            prop.separatorGroup = SeparatorGroup;
            properties.push(prop);
        }
        if (this.TerceraChartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.FitDayHighLow)).Visible &&
            typeof TerceraChartDayHighLowRenderer === 'function') {
            const highLowRenderer = mainWindow.GetRenderer(TerceraChartDayHighLowRenderer);
            if (highLowRenderer) {
                prop = new DynProperty('FitHighLow', highLowRenderer.UseInAutoscale, DynProperty.BOOLEAN, DynProperty.PRICE_SCALE_GROUP);
                prop.enabilityValue = true;
                prop.enabled = mainWindow.AutoScale;
                prop.sortIndex = 3;
                prop.separatorGroup = SeparatorGroup;
                properties.push(prop);
            }
        }
        const toolsRenderer = mainWindow.GetRenderer(TerceraChartToolRenderer);
        prop = new DynProperty('FitDrawings', toolsRenderer.UseInAutoscale, DynProperty.BOOLEAN, DynProperty.PRICE_SCALE_GROUP);
        prop.enabilityValue = true;
        prop.enabled = mainWindow.AutoScale;
        prop.sortIndex = 4;
        prop.separatorGroup = SeparatorGroup;
        properties.push(prop);
        if (this.TerceraChartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.FitAlerts)).Visible) {
            prop = new DynProperty('FitAlerts', this.TerceraChartTradingToolsRenderer.AlertScale, DynProperty.BOOLEAN, DynProperty.PRICE_SCALE_GROUP);
            prop.enabilityValue = true;
            prop.enabled = mainWindow.AutoScale;
            prop.sortIndex = 5;
            prop.separatorGroup = SeparatorGroup;
            properties.push(prop);
        }
        properties = properties.concat(this.model.Properties());
        properties = properties.concat(this.yScaleRendererSettings.Properties());
        properties = properties.concat(this.xScaleRendererSettings.Properties());
        return properties;
    }
    callBack(properties) {
        super.callBack(properties);
        const mainWindow = this.mainWindow;
        // #region Scale type
        let dp = DynProperty.getPropertyByName(properties, 'relativeScaleType');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(this.getActionByDataType(dp.value), mainWindow));
        }
        dp = DynProperty.getPropertyByName(properties, 'relativeCalcType');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            if (dp.value === TerceraChartCashItemSeriesDataTypeBasisType.BeginOfData) {
                this.cashItemSeriesSettings.BasisType = TerceraChartCashItemSeriesDataTypeBasisType.BeginOfScreen;
            }
            else {
                this.cashItemSeriesSettings.BasisType = dp.value;
            }
        }
        dp = DynProperty.getPropertyByName(properties, 'logBase');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value))
            this.cashItemSeriesSettings.logDataConverter.LogBase = dp.value;
        // #endregion
        dp = DynProperty.getPropertyByName(properties, 'autoYScale');
        if (dp) {
            this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(dp.value ? TerceraChartActionEnum.AutoScale : TerceraChartActionEnum.ManualScale, mainWindow));
        }
        dp = DynProperty.getPropertyByName(properties, 'xScale');
        if (dp)
            mainWindow.XScale = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'FitHighLow');
        if (dp) {
            const highLowRenderer = mainWindow.GetRenderer(TerceraChartDayHighLowRenderer);
            if (highLowRenderer)
                highLowRenderer.UseInAutoscale = dp.value;
        }
        dp = DynProperty.getPropertyByName(properties, 'FitIndicator');
        if (dp)
            mainWindow.indicatorRendererSettings.UseInAutoscale = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'FitOrders');
        if (dp)
            this.TerceraChartTradingToolsRenderer.OrderScale = dp.value;
        dp = DynProperty.getPropertyByName(properties, 'FitDrawings');
        if (dp) {
            const toolRenderer = mainWindow.GetRenderer(TerceraChartToolRenderer);
            toolRenderer.UseInAutoscale = dp.value;
        }
        dp = DynProperty.getPropertyByName(properties, 'FitAlerts');
        if (dp)
            this.TerceraChartTradingToolsRenderer.AlertScale = dp.value;
        this.yScaleRendererSettings.callBack(properties);
        this.xScaleRendererSettings.callBack(properties);
        // в самом конце загрузка пропертей модели и перегруз чарта
        this.model.callBack(properties);
        this.windowsContainer.LayoutWindows();
        this.IsDirty();
    }
    // #endregion
    checkBarsCountAfterEvents() {
        void this.autoLoadManager.update();
    }
    CheckBarsCountOnScreen() {
        this.moveScreenByX(this.rightBarIndexIncludingEmptyBars);
        void this.autoLoadManager.update();
    }
    // #region EventsProcessors
    ProcessMouseUp(e) {
        let mouseProcessed = false;
        try {
            // Cancel action on RightButton
            if (e.Button === MouseButtons.Right && this.CancelAction(CancelActionContext.RightButton)) {
                return true;
            }
            else if (this.HasCorrectData()) {
                this.autoLoadManager.processMouseUp();
            }
            mouseProcessed = super.ProcessMouseUp(e);
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
        }
        return mouseProcessed;
    }
    OnKeyDown() {
        const keyProc = KeyEventProcessor;
        const currentButton = keyProc.currentButton;
        if (keyProc.isCtrlModOnly() && currentButton === KeyCode.A) {
            const toolsRenderer = this.mainWindow.GetRenderer(TerceraChartToolRenderer);
            toolsRenderer.SelectAllTools();
            this.IsDirty(LayersEnum.Tools);
            return;
        }
        switch (currentButton) {
            case KeyCode.ESC:
                this.CancelAction(CancelActionContext.Esc);
                break;
            case KeyCode.DELETE:
                this.RemoveSelectedAndHoveredToolsFromAllWindows();
                this.IsDirty(LayersEnum.Tools);
                break;
        }
    }
    // #endregion
    // #region Cursor
    GetCursor(e) {
        if (this.MouseState === TerceraChartMouseState.Moving && this.HasCorrectData()) {
            const activeWindow = this.ActiveOverlayGet() != null ? this.ActiveOverlayGet().window : this.HoverWindow;
            if (activeWindow != null) {
                return activeWindow.AutoScale ? Cursors.SizeWE : Cursors.SizeAll;
            }
        }
        return super.GetCursor(e);
    }
    // #endregion
    // #region Window
    CreateWindowsContainer() {
        const scrollerRenderer = new TerceraChartScrollerRenderer(this);
        const xScaleRenderer = new TerceraChartTimeScaleRenderer(this.TerceraChartActionProcessor, this.xScaleRendererSettings, this);
        const zoomRenderer = new TerceraChartZoomRenderer(this, this.TerceraChartActionProcessor);
        const windowsContainer = new TerceraChartWindowsContainer(scrollerRenderer, xScaleRenderer, zoomRenderer);
        windowsContainer.AfterResizeWindows.Subscribe(this.windowsContainer_AfterResizeWindows, this);
        return windowsContainer;
    }
    CreateMainWindow() {
        const priceScaleRender = this.GetChartPriceScaleRenderer(this, this.yScaleRendererSettings);
        const leftPriceScaleRender = this.GetChartPriceScaleRenderer(this, this.yScaleRendererSettings);
        leftPriceScaleRender.Aligment = TerceraChartPriceScaleRendererAligment.Left;
        const window = this.GetChartWindow(priceScaleRender, leftPriceScaleRender);
        window.PointsConverter = new CashItemPointsCoverter(window, null);
        return window;
    }
    InitializeRenderers() {
        const newToolRenderer = this.GetChartNewToolRenderer();
        const toolsRenderer = this.GetChartToolRenderer(newToolRenderer);
        if (!isNullOrUndefined(toolsRenderer)) {
            toolsRenderer.InstanceWindow = this.mainWindow;
            this.mainWindow.Renderers.push(toolsRenderer);
        }
        this.mainPriceRenderer = new TerceraChartMainPriceRenderer(this);
        this.mainPriceRenderer.cashSettings = this.cashItemSeriesSettings;
        // there was an overlay in the wrong order
        this.volumeBarsRenderer = new TerceraChartVolumeBarsRenderer(this.cashItemSeriesSettings, this);
        this.mainWindow.Renderers.push(this.volumeBarsRenderer);
        this.mainWindow.Renderers.push(new TerceraChartTimeScaleSeparatorsRenderer(this, this.windowsContainer.xScaleRenderer));
        this.overlayStorageRenderer = new TerceraChartOverlayStorageRenderer(this);
        this.mainWindow.Renderers.push(this.overlayStorageRenderer);
        const indicatorStorageRenderer = new TerceraChartIndicatorStorageRenderer(this, false);
        this.mainWindow.Renderers.push(indicatorStorageRenderer);
        this.mainWindow.Renderers.push(this.mainPriceRenderer);
        this.mainWindow.Renderers.push(newToolRenderer); // помещаю ньютуллзрендерер после майнпрайса, чтобы тулзы поверх баров рисовались
        this.mainWindow.Renderers.push(indicatorStorageRenderer); // Один рендерер кладем два раза - до mainPriceRenderer и после. Реализуем OnTop у индикаторов.
        this.mainWindow.Renderers.push(new TerceraChartScalingByRectangleRenderer(this));
        this.TerceraChartWatermarkRenderer = new TerceraChartWatermarkRenderer(this);
        this.mainWindow.Renderers.splice(0, 0, this.TerceraChartWatermarkRenderer);
        this.DayHighLowRenderer = new TerceraChartDayHighLowRenderer(this);
        this.mainWindow.Renderers.push(this.DayHighLowRenderer);
        this.TerceraChartTimeToNextBarRenderer = this.GetTerceraChartTimeToNextBarRenderer();
        this.mainWindow.Renderers.push(this.TerceraChartTimeToNextBarRenderer);
        this.SpreadRenderer = new TerceraChartSpreadRenderer(this);
        this.mainWindow.Renderers.push(this.SpreadRenderer);
        this.PortfolioChartReturnsRenderer = new PortfolioChartReturnsRenderer(this);
        this.HighlightExtendedSessionRenderer = this.GetChartHighlightExtendedSessionRenderer(this);
        this.mainWindow.Renderers.splice(0, 0, this.HighlightExtendedSessionRenderer, this.PortfolioChartReturnsRenderer);
        this.TerceraChartTradingToolsRenderer = this.GetChartTradingToolsRenderer(newToolRenderer);
        this.mainWindow.Renderers.push(this.TerceraChartTradingToolsRenderer);
        const toolRenderer = this.mainWindow.GetRenderer(TerceraChartToolRenderer);
        toolRenderer.SelectedToolsListChanged.Subscribe(this.ToolsRenderer_SelectedToolsListChanged, this);
        this.TerceraChartCrossHairRenderer = this.GetTerceraChartCrossHairRenderer();
        this.mainWindow.Renderers.push(this.TerceraChartCrossHairRenderer);
        if (DataCache.AlertManager.AlertsVisible) {
            this.TerceraChartNewAlertRenderer = new TerceraChartNewAlertRenderer(this);
            this.mainWindow.Renderers.push(this.TerceraChartNewAlertRenderer);
        }
        this.SelectionRenderer = new TerceraChartSelectionRenderer(this);
        this.EraserRenderer = new TerceraChartEraserRenderer(this);
        this.mainWindow.Renderers.push(this.SelectionRenderer);
        this.windowsContainer.Renderers.push(this.EraserRenderer);
        this.TerceraChartInfoWindowRenderer = new TerceraChartInfoWindowRenderer(this, this.mainWindow);
        this._forceUpdateInitiators.push(this.TerceraChartInfoWindowRenderer);
        this.mainWindow.Renderers.push(this.TerceraChartInfoWindowRenderer);
        this.TradingCentralRenderer = new TradingCentralLinesRenderer(this); // #97276 for TradingCentral only, Visible = false by default
        this.mainWindow.Renderers.push(this.TradingCentralRenderer);
        this.goToTabletRenderer = new GoToTabletRenderer(this);
        this.mainWindow.Renderers.push(this.goToTabletRenderer);
    }
    GetActiveWindow() {
        return this.FocusedOverlay != null ? this.FocusedOverlay.window : super.GetActiveWindow();
    }
    GetActiveWindowFromEvent(e) {
        return this.FocusedOverlay != null ? this.FocusedOverlay.window : super.GetActiveWindowFromEvent(e);
    }
    GetLastMouseDownWindow() {
        return this.FocusedOverlay != null ? this.FocusedOverlay.window : super.GetLastMouseDownWindow();
    }
    moveScreenByX(newI1) {
        const mainCashItemSeries = this.mainPriceRenderer.Series;
        if (mainCashItemSeries === null) {
            return false;
        }
        const mainWindow = this.mainWindow;
        const barsOnScreen = Math.floor(mainWindow.im());
        const barsCount = this.BarsCount();
        const leftLimit = barsOnScreen - mainCashItemSeries.rightPaddingBarsCount;
        if (newI1 < leftLimit) {
            newI1 = Math.max(newI1, this.minRealBarsCount - 1);
        }
        this.autoLoadManager.processDragLeft(newI1);
        const rightLimit = barsCount - mainCashItemSeries.rightPaddingBarsCount - mainCashItemSeries.leftPaddingBarsCount - 1;
        if (newI1 >= rightLimit) {
            const maxIndex = Math.floor(barsCount - mainCashItemSeries.rightPaddingBarsCount + barsOnScreen - this.minRealBarsCount);
            newI1 = Math.min(newI1, maxIndex);
        }
        this.autoLoadManager.processDragRight(newI1);
        return super.moveScreenByX(newI1);
    }
    ToBegin() {
        const cashItem = this.MainCashItemSeries.CashItem;
        if (cashItem.isRealTimeBuilding) {
            super.ToBegin();
        }
        else {
            void this.LoadHistoryStart(ChartLoadTrigger.RefreshAction);
        }
    }
    BarsCount() {
        var _a;
        const mainCashItemSeries = (_a = this.mainPriceRenderer) === null || _a === void 0 ? void 0 : _a.Series;
        if (!isNullOrUndefined(mainCashItemSeries)) {
            return mainCashItemSeries.Count();
        }
        else {
            return super.BarsCount();
        }
    }
    HasCorrectData() {
        var _a;
        return (((_a = this.MainCashItemSeries) === null || _a === void 0 ? void 0 : _a.CashItem.FNonEmptyCashArrayCount) > 0 && !this.LoadingNow && super.HasCorrectData());
    }
    CalculateMinMax() {
        super.CalculateMinMax();
        if (this.overlayStorageRenderer != null) {
            this.overlayStorageRenderer.CalculateMinMax();
        }
    }
    FindIntervalExactly(dataX) {
        return this.mainPriceRenderer.Series.FindIntervalExactly(dataX);
    }
    calulateNewI1FromDelta(deltaX) {
        const correctedDeltaX = deltaX / (this.mainWindow.ClientRectangle.Width / this.mainWindow.im());
        return this.lastMouseDownI1 - correctedDeltaX;
    }
    // #endregion
    // #region Context menu
    GetContextMenuItems(e, chart) {
        const menuItems = super.GetContextMenuItems(e, chart);
        if (isValidArray(menuItems)) {
            return menuItems;
        }
        const actionsProcessor = this.TerceraChartActionProcessor;
        // ObjectManager
        menuItems.push(actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.ObjectManager)));
        menuItems.push({ type: NewContextMenuItemType.Separator });
        // Style
        menuItems.push(Object.assign(Object.assign({}, actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Style))), { subitems: [
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Style, TerceraChartDrawingType.Renko)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Style, TerceraChartDrawingType.LinesBreak)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Style, TerceraChartDrawingType.Line)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Style, TerceraChartDrawingType.Bar)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Style, TerceraChartDrawingType.Candle)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Style, TerceraChartDrawingType.Dot)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Style, TerceraChartDrawingType.DotLine)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Style, TerceraChartDrawingType.Forest)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Style, TerceraChartDrawingType.Solid)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Style, TerceraChartDrawingType.Kagi)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Style, TerceraChartDrawingType.Profile)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Style, TerceraChartDrawingType.TicTac)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Style, TerceraChartDrawingType.Cluster))
            ] }));
        // Info Window
        menuItems.push(Object.assign(Object.assign({}, actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.InfoWindowMode))), { subitems: [
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.InfoWindowMode, InfoWindowDisplayMode.SeparateWindow)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.InfoWindowMode, InfoWindowDisplayMode.AttachedToCursor)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.InfoWindowMode, InfoWindowDisplayMode.Hidden))
            ] }));
        // DataType
        menuItems.push(Object.assign(Object.assign({}, actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DataType))), { subitems: [
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DataType, new TerceraChartHistoryType(ChartHistoryType.Default))),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DataType, new TerceraChartHistoryType(ChartHistoryType.ByBid))),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DataType, new TerceraChartHistoryType(ChartHistoryType.ByBidAsk))),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DataType, new TerceraChartHistoryType(ChartHistoryType.ByTrades))),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DataType, new TerceraChartHistoryType(ChartHistoryType.ByASk)))
            ] }));
        menuItems.push({ type: NewContextMenuItemType.Separator });
        // Overlays
        menuItems.push(Object.assign(Object.assign({}, actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Overlay))), { subitems: [
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Overlay, ITerceraChartShowScreenType.OverlayScreen)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Overlay, true))
            ] }));
        // Indicators
        menuItems.push(Object.assign(Object.assign({}, actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Indicator))), { subitems: [
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Indicator, ITerceraChartShowScreenType.ScriptLookup)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Indicator, true))
            ] }));
        // Tools
        menuItems.push(Object.assign(Object.assign({}, actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool))), { subitems: [
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.ToolSelector)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Eraser)),
                { type: NewContextMenuItemType.Separator },
                Object.assign(Object.assign({}, actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.TrendTools))), { subitems: [
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.HorizontalLine)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.VerticalLine)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.Line)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.PriceChannel)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.RegressionChannel)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.AndrewsPitchFork))
                    ] }),
                Object.assign(Object.assign({}, actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.GeometryTools))), { subitems: [
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.Triangle)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.Rectangle)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.Circle)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.Ellipse)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.Polygon))
                    ] }),
                Object.assign(Object.assign({}, actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.FibonacciTools))), { subitems: [
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.FibonacciRetracement)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.FibonacciExpansion)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.FibonacciArc)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.FibonacciEllipse)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.FibonacciSpiral)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.FibonacciFans)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.FibonacciPhiChannel)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.FibonacciTimeGoal)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.FibonacciTimeZone))
                    ] }),
                Object.assign(Object.assign({}, actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.GannTools))), { subitems: [
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.GannLine)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.GannFan)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.GannGrid))
                    ] }),
                Object.assign(Object.assign({}, actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.ElliottTools))), { subitems: [
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.ElliottImpulse)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.ElliottCorrection)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.ElliottTriangle)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.ElliottTripleCombo)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.ElliottDoubleCombo))
                    ] }),
                Object.assign(Object.assign({}, actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.SecondaryTools))), { subitems: [
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.InfoLine)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.ArrowLabel)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.CommentsLabel)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.PriceLabel))
                    ] }),
                Object.assign(Object.assign({}, actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType))), { subitems: [
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.HorizontalLine)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.VerticalLine)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.Line)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.PriceChannel)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.RegressionChannel)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.AndrewsPitchFork)),
                        { type: NewContextMenuItemType.Separator },
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.Triangle)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.Rectangle)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.Circle)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.Ellipse)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.Polygon)),
                        { type: NewContextMenuItemType.Separator },
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.FibonacciRetracement)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.FibonacciExpansion)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.FibonacciArc)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.FibonacciEllipse)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.FibonacciSpiral)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.FibonacciFans)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.FibonacciPhiChannel)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.FibonacciTimeGoal)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.FibonacciTimeZone)),
                        { type: NewContextMenuItemType.Separator },
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.GannLine)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.GannFan)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.GannGrid)),
                        { type: NewContextMenuItemType.Separator },
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.ElliottImpulse)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.ElliottCorrection)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.ElliottTriangle)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.ElliottDoubleCombo)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.ElliottTripleCombo)),
                        { type: NewContextMenuItemType.Separator },
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.InfoLine)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.ArrowLabel)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.CommentsLabel)),
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteToolsByType, DataCacheToolType.PriceLabel)),
                        { type: NewContextMenuItemType.Separator },
                        actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteAllTools))
                    ] })
            ] }));
        if (!Resources.isHidden('allowCreatePanelsFromPanel')) {
            // New
            const subitems = [];
            if (!Resources.isHidden('chart.new.oe')) {
                subitems.push(actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.NewPanel, PanelNames.AdvancedOrderEntry)));
            }
            if (!Resources.isHidden('chart.new.level2')) {
                subitems.push(actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.NewPanel, PanelNames.MarketDepthPanel)));
            }
            if (subitems.length > 0) {
                menuItems.push(Object.assign(Object.assign({}, actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.NewPanel))), { subitems: subitems }));
            }
        }
        /// / View
        menuItems.push(Object.assign(Object.assign({}, actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.View, null, false))), { subitems: [
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.PreviousCloseLine, false, true)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.TimeToNextBar, false, true)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.DayHighLow, false, true)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.Volume, false, true)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.GridTime, false, true)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.GridPrice, false, true)),
                { type: NewContextMenuItemType.Separator },
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.AnalyseToolBar, false, true)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.DrawingToolBar, false, true)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.MainToolBar, false, true)),
                actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.OrderEntry, false, true))
            ] }));
        menuItems.push({ type: NewContextMenuItemType.Separator });
        menuItems.push(actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Refresh)));
        menuItems.push(actionsProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.Settings)));
        return menuItems;
    }
    // #endregion
    // #region  Drawing
    GetAdvancedParamsForRenderer(gr) {
        const params = super.GetAdvancedParamsForRenderer(gr);
        params.Instrument = this.Instrument();
        params.Account = this.Account();
        params.mainPriceRenderer = this.mainPriceRenderer;
        return params;
    }
    GetYScaleRectangles() {
        const res = super.GetYScaleRectangles();
        // Process overlays
        this.Overlays.forEach((overlay) => {
            if (overlay.window.leftYScaleRenderer.Rectangle.Width > 0) {
                res.push(overlay.window.leftYScaleRenderer.Rectangle);
            }
        });
        return res;
    }
    GetTerceraChartPriceScaleLayoutInfo(gr) {
        var _a;
        const res = new TerceraChartPriceScaleLayoutInfo();
        // Right scale
        let rightMaxWidth = 0;
        for (let i = 0; i < this.windowsContainer.Windows.length; i++) {
            const curW = this.windowsContainer.Windows[i].rightYScaleRenderer.GetPreferredWidth(gr);
            if (rightMaxWidth < curW) {
                rightMaxWidth = curW;
            }
        }
        // Celing to 5 pixels
        const ceilingVal = Math.floor(Math.ceil(rightMaxWidth / 5.0) * 5);
        res.PreferredWidthScales =
            ceilingVal > TerceraChartPriceScaleRenderer.MIN_WIDTH
                ? ceilingVal
                : TerceraChartPriceScaleRenderer.MIN_WIDTH;
        for (let i = 1; i < this.windowsContainer.Windows.length; i++) {
            // Indicator digits
            const priceScaleRend = this.windowsContainer.Windows[i]
                .rightYScaleRenderer;
            let digitsBasedOnIndicator = -1;
            const renderer = this.windowsContainer.Windows[i].GetRenderer(TerceraChartIndicatorStorageRenderer);
            for (let j = 0; j < renderer.Indicators.length; j++) {
                const rend = renderer.Indicators[j];
                // Get maximum from all indicators for current window
                if ((rend === null || rend === void 0 ? void 0 : rend.PTLIndicator) != null &&
                    rend.PTLIndicator.Digits != -1 &&
                    rend.PTLIndicator.Digits < 8 &&
                    rend.PTLIndicator.Digits > digitsBasedOnIndicator) {
                    digitsBasedOnIndicator = rend.PTLIndicator.Digits;
                }
            }
            priceScaleRend.DigitsBasedOnIndicator = digitsBasedOnIndicator;
        }
        // Left scale
        // 1. Наличие оверлея включает левую шкалу для всего чарта
        let leftMaxWidth = 0;
        if (this.Overlays.length > 0) {
            leftMaxWidth = 0;
            for (let i = 0; i < this.Overlays.length; i++) {
                // Not loaded yet
                if (((_a = this.Overlays[i].mainPriceRenderer) === null || _a === void 0 ? void 0 : _a.Series) == null || !this.Overlays[i].separateScale) {
                    continue;
                }
                const curW = this.Overlays[i].window.leftYScaleRenderer.GetPreferredWidth(gr);
                if (leftMaxWidth < curW) {
                    leftMaxWidth = curW;
                }
            }
            // Celing to 5 pixels
            res.PreferredWidthScalesLeft = Math.floor(Math.ceil(leftMaxWidth / 5.0) * 5);
            res.HasOverlays = res.PreferredWidthScalesLeft > 0;
            // Аналитик попросил одинаковую ширину
            if (res.HasOverlays) {
                res.PreferredWidthScalesLeft = res.PreferredWidthScales = Math.max(res.PreferredWidthScales, res.PreferredWidthScalesLeft);
            }
        }
        else {
            res.HasOverlays = false;
        }
        return res;
    }
    SynchronizeBeforeDraw() {
        var _a;
        super.SynchronizeBeforeDraw();
        const mainCashItemSeries = this.mainPriceRenderer.Series;
        if (mainCashItemSeries != null) {
            const firstBarOnTheScreenIndex = Math.floor(this.rightBarIndexIncludingEmptyBars - this.mainWindow.im()) + 1;
            const lastBarOnTheScreenIndex = Math.ceil(this.rightBarIndexIncludingEmptyBars);
            // Cache cashitem data to array for quick acces
            mainCashItemSeries.CacheScreenData(firstBarOnTheScreenIndex, lastBarOnTheScreenIndex, this.Instrument());
            // Overlays:
            // - new logic: automatically set ActiveOverlay if null
            const Overlays = this.Overlays;
            if (this.ActiveOverlayGet() == null || !this.ActiveOverlayGet().SeparateScaleGet()) {
                // need reset - search first correct overlay
                let newOverlay = null;
                for (let i = 0; i < Overlays.length; i++) {
                    if (Overlays[i].SeparateScaleGet()) {
                        newOverlay = Overlays[i];
                        break;
                    }
                }
                this.ActiveOverlaySet(newOverlay);
            }
            // Synronize by active overlays
            if (this.activeOverlay != null) {
                for (let i = 0; i < Overlays.length; i++) {
                    if (Overlays[i] == this.activeOverlay || !Overlays[i].SeparateScaleGet()) {
                        continue;
                    }
                    Overlays[i].cashItemSeriesSettings.DataType = this.activeOverlay.cashItemSeriesSettings.DataType;
                    Overlays[i].window.AutoScale = this.ActiveOverlayGet().window.AutoScale;
                }
            }
            // - Cache cashitem data for overlay
            // - Set visible to active PriceScaleRenderer
            this.mainWindow.leftYScaleRenderer.Visible = this.ActiveOverlayGet() == null;
            for (let i = 0; i < Overlays.length; i++) {
                const overlay = Overlays[i];
                overlay.window.XScale = this.mainWindow.XScale;
                overlay.window.i1 = this.mainWindow.i1;
                overlay.PriceScaleRenderer.Visible =
                    this.ActiveOverlayGet() === Overlays[i] && this.ActiveOverlayGet().SeparateScaleGet();
                if (((_a = overlay.mainPriceRenderer) === null || _a === void 0 ? void 0 : _a.Series) != null) {
                    overlay.mainPriceRenderer.Series.CacheScreenData(firstBarOnTheScreenIndex, lastBarOnTheScreenIndex, overlay.ActiveInstrument);
                }
            }
        }
    }
    DrawNoData(gr) {
        const instrument = this.Instrument();
        const timeFrameInfo = this.TimeFrameInfo();
        const hasData = !isNullOrUndefined(instrument) && !isNullOrUndefined(timeFrameInfo);
        let message;
        if (hasData) {
            const resourceKey = this.LoadingNow ? 'chart.loadingData' : 'chart.noAvailableData';
            message = `${Resources.getResource(resourceKey)} ${instrument.DisplayName()} ${Periods.getTicNameShort(timeFrameInfo.Periods)} ...      `;
        }
        else {
            message = Resources.getResource('chart.noAvailableData');
        }
        this.NoDataMessage = message;
        super.DrawNoData(gr);
    }
    Draw() {
        if (isNullOrUndefined(this.mainPriceRenderer))
            return;
        super.Draw();
    }
    // #endregion
    get ShowAllSessions() {
        return this.model.GetShowAllSessions();
    }
    Instrument() {
        return this.model.GetInstrument();
    }
    Account() {
        return this.model.GetAccount();
    }
    TimeFrameInfo() {
        return this.model.GetTimeFrameInfo();
    }
    ChartHistoryType() {
        return this.model.GetChartHistoryType();
    }
    get MainCashItemSeries() {
        var _a, _b;
        return (_b = (_a = this.mainPriceRenderer) === null || _a === void 0 ? void 0 : _a.Series) !== null && _b !== void 0 ? _b : null;
    }
    GetChartNewToolRenderer() {
        return new TerceraChartNewToolRenderer(this, this.NewToolStrategy, this.ChartID);
    }
    GetChartToolRenderer(newToolRenderer) {
        return new TerceraChartToolRenderer(this.ChartID, this, newToolRenderer);
    }
    GetChartTradingToolsRenderer(newToolRenderer) {
        return new TerceraChartTradingToolsRenderer(this.terceraChartPanelContext, this, this.mainWindow, newToolRenderer);
    }
    GetTerceraChartTimeToNextBarRenderer() {
        return new TerceraChartTimeToNextBarRenderer(this);
    }
    GetChartPriceScaleRenderer(terceraChart, settings, windowType = TerceraChartPriceScaleRendererWindowType.Main) {
        return new TerceraChartPriceScaleRenderer(terceraChart, settings, windowType);
    }
    GetChartPriceScaleRendererSettings() {
        return new TerceraChartPriceScaleRendererSettings();
    }
    GetTerceraChartCrossHairRenderer() {
        return new TerceraChartCrossHairRenderer(this);
    }
    GetChartTimeScaleRendererSettings() {
        return new TerceraChartTimeScaleRendererSettings();
    }
    GetChartWindow(priceScaleRender, leftPriceScaleRender) {
        return new TerceraChartWindow(priceScaleRender, leftPriceScaleRender);
    }
    GetCharIndicatorRenderer(indicator, terceraChart) {
        return new TerceraChartIndicatorRenderer(indicator, terceraChart);
    }
    GetCharPlateIndicatorRenderer(terceraChart) {
        return new TerceraChartPlateRenderer(terceraChart);
    }
    GetChartHighlightExtendedSessionRenderer(terceraChart) {
        return new TerceraChartHighlightExtendedSessionRenderer(terceraChart);
    }
    // #region LoadingData
    _autoLoadingStateChanged(state) {
        this.autoLoadingStateChanged.Raise(state);
    }
    isTimeInHistoryRange(time) {
        if (isNullOrUndefined(time)) {
            return false;
        }
        const cashItemSeries = this.MainCashItemSeries;
        if (isNullOrUndefined(cashItemSeries)) {
            return false;
        }
        let toTime = null;
        if (cashItemSeries.CashItem.isRealTimeBuilding) {
            const now = new Date();
            toTime = new Date(now.setHours(23, 59, 59, 999));
        }
        else {
            toTime = cashItemSeries.To;
        }
        return cashItemSeries.From.getTime() <= time.getTime() && time.getTime() < toTime.getTime();
    }
    goToPositionBytime(time) {
        if (isNullOrUndefined(time)) {
            return;
        }
        const cashItemSeries = this.MainCashItemSeries;
        if (isNullOrUndefined(cashItemSeries)) {
            return;
        }
        const indexGoTo = cashItemSeries.FindIntervalExactly(time.getTime());
        if (indexGoTo === NOT_FOUND_INTERVAL_VALUE) {
            return;
        }
        const barsOnScreen = Math.floor(this.mainWindow.im());
        const midIndex = Math.floor(indexGoTo + Math.floor(barsOnScreen / 2)) - cashItemSeries.leftPaddingBarsCount;
        this.ToCustomPositionOnChart(midIndex);
    }
    LoadHistoryStart(triggerAction, midDate) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            this.OnLoadingStart.Raise();
            this.LoadingNow = true;
            this.IsDirty();
            (_a = this.loadHistoryAbortController) === null || _a === void 0 ? void 0 : _a.abort();
            this.loadHistoryAbortController = new AbortController();
            const signal = this.loadHistoryAbortController.signal;
            const historyParams = this.GetHistoryParams(null, null, triggerAction, midDate);
            const ins = this.Instrument();
            let newCashItem = null;
            if (triggerAction === ChartLoadTrigger.LoadingRelativelyMidDate) {
                const cashItemSeries = this.MainCashItemSeries;
                newCashItem = yield CashItem.CreateWithHistory(ins, historyParams, signal, true, true, false, cashItemSeries.CashItem);
            }
            else {
                newCashItem = yield CashItem.CreateWithHistory(ins, historyParams, signal);
            }
            if (signal.aborted || newCashItem == null) {
                return;
            }
            this.LoadHistoryFinish(newCashItem, false, triggerAction, historyParams);
            this.moveScreenByX(this.rightBarIndexIncludingEmptyBars);
            this.IsDirty();
        });
    }
    getMinBarsForInd() {
        const allIndicators = this.IndicatorRenderers();
        let minBarsForInd = 0;
        for (let i = 0; i < allIndicators.length; i++) {
            const ind = allIndicators[i];
            minBarsForInd = Math.max(ind.getMinBarsCount(), minBarsForInd);
        }
        minBarsForInd++;
        return minBarsForInd;
    }
    LoadHistoryFinish(newCashItem, isAutoLoadHistory = false, triggerAction = ChartLoadTrigger.ScrollingLeft, historyParams) {
        this.ReplaceCashItem(newCashItem);
        if (triggerAction === ChartLoadTrigger.LoadingPeriodChanged) {
            this.ToCustomPositionOnChart(0);
        }
        else if (triggerAction === ChartLoadTrigger.LoadingRelativelyMidDate) {
            this.goToNearestTime(historyParams.FromTime);
        }
        else if (!isAutoLoadHistory) {
            this.ToBegin();
        }
        // Не рисуем пока оверлеи - они не валидны
        for (let i = 0; i < this.Overlays.length; i++) {
            this.Overlays[i].mainPriceRenderer.InvalidState = true;
        }
        //
        // Replace indicators
        //
        const allIndicators = this.IndicatorRenderers();
        for (let i = 0; i < allIndicators.length; i++) {
            allIndicators[i].InvalidState = true;
        }
        this.addIndicatorsToCashItem(allIndicators);
        // To set correct min/max
        if (!this.mainWindow.AutoScale && !isAutoLoadHistory) {
            this.mainWindow.AutoFit();
        }
        this.LoadingNow = false;
        this.OnLoadingFinish.Raise();
        if (!isAutoLoadHistory) {
            this.UpdateOverlay(triggerAction);
        }
        this.IsDirty();
    }
    unsubscribeCashItemFromInstrument() {
        var _a, _b;
        const cashItem = (_b = (_a = this.MainCashItemSeries) === null || _a === void 0 ? void 0 : _a.CashItem) !== null && _b !== void 0 ? _b : null;
        if (!isNullOrUndefined(cashItem)) {
            cashItem.UnSubscribeCurrentInstrument();
        }
    }
    // TODO. Refactor.
    // Syncs chart's mainPriceRenderer and overlay's mainPriceRenderer data for drawing series.
    // If overlay's data is updated faster than data from chart's mainPriceRenderer,
    // overlay's last bar won't be drawn on chart, since overlay's screen data depends on amount of data from main series.
    recalculateOverlaysIndices() {
        try {
            const overlays = this.Overlays;
            for (let i = 0, len = overlays.length; i < len; i++) {
                const overlay = overlays[i];
                overlay.recalculateIndices();
            }
        }
        catch (e) {
            ErrorInformationStorage.GetException(e);
            // console.error(e)
        }
    }
    // TODO. Refactor.
    LoadOverlayCashItem(overlay, mainChartTriggerAction) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            let tfInfo = this.TimeFrameInfo();
            const account = this.Account();
            const spreadPlan = DataCache.GetSpreadPlan(account);
            tfInfo = tfInfo.Copy({ historyType: overlay.HistoryType, spreadPlan });
            const ins = overlay.ActiveInstrument;
            const historyParams = this.GetHistoryParams(ins, tfInfo, ChartLoadTrigger.LoadingPeriodChanged);
            (_a = this.loadOverlayHistoryAbortController) === null || _a === void 0 ? void 0 : _a.abort();
            this.loadOverlayHistoryAbortController = new AbortController();
            const signal = this.loadOverlayHistoryAbortController.signal;
            let newCashItem = null;
            if (mainChartTriggerAction === ChartLoadTrigger.LoadingRelativelyMidDate) {
                const cashItemSeries = overlay.mainPriceRenderer.Series;
                newCashItem = yield CashItem.CreateWithHistory(ins, historyParams, signal, true, true, false, cashItemSeries.CashItem);
            }
            else {
                newCashItem = yield CashItem.CreateWithHistory(ins, historyParams, signal);
            }
            if (signal.aborted || newCashItem == null) {
                // new load history started
                return;
            }
            this.OnLoadOverlayCashItemFinish(overlay, newCashItem);
        });
    }
    OnLoadOverlayCashItemFinish(overlay, newCashItem, isAutoLoadHistory = false) {
        const oldSeries = overlay.mainPriceRenderer.Series;
        if (!isNullOrUndefined(oldSeries)) {
            oldSeries.HistoryExpanded.UnSubscribe(this.IsDirtyHist, this);
        }
        // Создаем серию
        const cashItemSeries = new TerceraChartOverlaySeries(newCashItem, overlay.cashItemSeriesSettings, this.mainPriceRenderer.Series);
        overlay.mainPriceRenderer.Series = cashItemSeries;
        overlay.window.PointsConverter = new CashItemPointsCoverter(overlay.window, cashItemSeries);
        cashItemSeries.HistoryExpanded.Subscribe(this.IsDirtyHist, this);
        // To set correct min/max
        if (overlay.window.FminFloatY === 0 && overlay.window.FmaxFloatY === 0) {
            overlay.window.AutoFit();
        }
        // Оверлей валиден - можно рисовать
        overlay.mainPriceRenderer.InvalidState = false;
        // Need for update OverlayWindow rectangle
        if (!isAutoLoadHistory) {
            this.layoutMainWindow();
            this.LayoutWindowsContainers();
        }
    }
    // #endregion
    UpdateNewOrderTool(data) {
        const TerceraChartNewToolRenderer = this.mainWindow.GetMainThreadRenderer('TerceraChartTradingToolsRenderer');
        if (TerceraChartNewToolRenderer) {
            TerceraChartNewToolRenderer.UpdateNewOrderTool(data);
        }
    }
    RemoveSelectedAndHoveredToolsFromAllWindows() {
        const windows = this.windowsContainer.Windows;
        const len = windows.length;
        for (let i = 0; i < len; i++) {
            const toolsRenderer = windows[i].GetRenderer(TerceraChartToolRenderer);
            if (!isNullOrUndefined(toolsRenderer)) {
                toolsRenderer.RemoveSelectedAndHoverTools();
            }
        }
    }
    MVCModelItemChanged(item) {
        const model = this.model;
        const windowsContainer = this.windowsContainer;
        switch (item.ItemType) {
            case ModelDataType.Style:
                this.mainPriceRenderer.ChartDrawingType = model.GetChartDrawingType();
                break;
            case ModelDataType.Instrument: {
                // Inform Renderers about changing instrument
                const newInstrument = model.GetInstrument();
                if (windowsContainer) {
                    for (let i = 0; i < windowsContainer.Windows.length; i++) {
                        const allRenderers = windowsContainer.Windows[i].GetAllRenderers();
                        for (let j = 0; j < allRenderers.length; j++) {
                            const renderer = allRenderers[j];
                            if (renderer.Instrument !== undefined) {
                                renderer.Instrument = newInstrument;
                            }
                        }
                    }
                }
                this.NeedSetDafaultsDataRage = true;
                break;
            }
            case ModelDataType.Account: {
                // Inform Renderers about changing account
                const newAcc = model.GetAccount();
                if (windowsContainer) {
                    for (let i = 0; i < windowsContainer.Windows.length; i++) {
                        const allRenderers = windowsContainer.Windows[i].GetAllRenderers();
                        for (let j = 0; j < allRenderers.length; j++) {
                            const renderer = allRenderers[j];
                            if (renderer.Account !== undefined) {
                                renderer.Account = newAcc;
                            }
                        }
                    }
                }
                break;
            }
        }
    }
    ReplaceCashItem(newCashItem) {
        var _a;
        if (isNullOrUndefined(this.model)) {
            return;
        }
        const oldSeries = (_a = this.mainPriceRenderer) === null || _a === void 0 ? void 0 : _a.Series;
        if (!isNullOrUndefined(oldSeries)) {
            oldSeries.HistoryExpanded.UnSubscribe(this.onMainSeriesHistoryExpanded, this);
        }
        const cashItemSeries = new TerceraChartCashItemSeries(newCashItem, this.cashItemSeriesSettings);
        this.mainPriceRenderer.Series = cashItemSeries;
        const timeScaleRender = this.windowsContainer.xScaleRenderer;
        timeScaleRender.Series = cashItemSeries;
        cashItemSeries.HistoryExpanded.Subscribe(this.onMainSeriesHistoryExpanded, this);
        const len = this.windowsContainer.Windows.length;
        for (let i = 0; i < len; i++) {
            const window = this.windowsContainer.Windows[i];
            window.PointsConverter = new CashItemPointsCoverter(window, cashItemSeries);
        }
        if (!isNullOrUndefined(oldSeries)) {
            // MB TODO fix refresh
            // https://tp.traderevolution.com/entity/102886
            // https://tp.traderevolution.com/entity/103018
            if (oldSeries._cashItem !== newCashItem) {
                const allIndicators = this.IndicatorRenderers();
                for (let i = 0; i < allIndicators.length; i++) {
                    oldSeries._cashItem.RemoveIndicator(allIndicators[i].Indicator);
                }
                oldSeries.Dispose();
            }
        }
        this.IsDirty();
    }
    onMainSeriesHistoryExpanded(hasNewBars) {
        const possibleNewIndex = Math.ceil(this.mainWindow.i1 + 1); // using this.mainWindow.i1, because it does not include leftPaddingBars
        if (hasNewBars && this.IsAtTheBegin(possibleNewIndex)) {
            this.moveScreenByX(this.rightBarIndexIncludingEmptyBars + 1);
        }
        // TODO. Refactor.
        this.recalculateOverlaysIndices();
        this.IsDirtyHist(hasNewBars);
    }
    IsDirtyHist(hasNewBars) {
        if (hasNewBars) {
            this.IsDirty(true);
        }
        else {
            this.IsDirty(LayersEnum.Quotes);
            this.IsDirty(LayersEnum.CrossHair);
        }
    }
    IsAtTheBegin(index) {
        const barsCount = this.BarsCount();
        if (barsCount === 0) {
            return true;
        }
        const cashItemSeries = this.MainCashItemSeries;
        return index >= barsCount - cashItemSeries.rightPaddingBarsCount - 1;
    }
    CancelAction(context) {
        return this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.Cancel, context));
    }
    goToAction(goToDate) {
        const cashItem = this.MainCashItemSeries.CashItem;
        if (this.isTimeInHistoryRange(goToDate) || (cashItem.isMaxHistoryLoaded && cashItem.isRealTimeBuilding)) {
            this.goToNearestTime(goToDate);
            this.CheckBarsCountOnScreen();
        }
        else {
            this.autoLoadManager.stopLoading();
            this.autoLoadManager.resetCurrentView();
            void this.LoadHistoryStart(ChartLoadTrigger.LoadingRelativelyMidDate, goToDate);
        }
    }
    RefreshChart(triggerAction = ChartLoadTrigger.RefreshAction) {
        void this.RefreshChartProcces(triggerAction);
        this.autoLoadManager.stopLoading();
        this.autoLoadManager.resetCurrentView();
    }
    RefreshChartProcces(triggerAction) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (isNullOrUndefined(this.Instrument()) || isNullOrUndefined(this.TimeFrameInfo())) {
                return;
            }
            yield this.LoadHistoryStart(triggerAction);
            (_a = this.TerceraChartTradingToolsRenderer) === null || _a === void 0 ? void 0 : _a.RepopulateTools();
            this.UpdateCursor();
        });
    }
    // #region Indicators
    AddIndicator(indicator, setupProperties = false) {
        if (indicator === null) {
            return false;
        }
        indicator.Symbol = this.Instrument().GetInteriorID();
        indicator.TimeFrameInfo = this.TimeFrameInfo();
        indicator.MainChart = this;
        // Edit properties
        const indicatorRenderer = this.GetCharIndicatorRenderer(indicator, this);
        if (setupProperties) {
            indicatorRenderer.editProperties(this);
        }
        else {
            this.AddIndicatorProcess(indicatorRenderer);
        }
        this.OnAddIndicator.Raise(indicatorRenderer);
    }
    AddIndicatorProcess(indicatorRenderer) {
        const indicator = indicatorRenderer.Indicator;
        indicator.Symbol = this.Instrument().GetInteriorID();
        indicator.TimeFrameInfo = this.TimeFrameInfo();
        indicator.MainChart = this;
        const ind = this.mainPriceRenderer.Series._cashItem.AddIndicator(indicator);
        if (ind !== null) {
            ind.Refresh();
        }
        indicatorRenderer.Series = this.mainPriceRenderer.Series;
        const render = this.mainWindow.GetRenderer(TerceraChartIndicatorStorageRenderer);
        render.Indicators.push(indicatorRenderer);
        this.CorrectIndicatorWindows();
    }
    addIndicatorsToCashItem(indicators) {
        var _a;
        const len = indicators.length;
        for (let i = 0; i < len; i++) {
            const indRenderer = indicators[i];
            let ind = indRenderer.Indicator;
            const oldCashItem = (_a = indRenderer.Series) === null || _a === void 0 ? void 0 : _a.CashItem;
            indRenderer.Series = this.mainPriceRenderer.Series;
            if (oldCashItem !== indRenderer.Series.CashItem) {
                ind = indRenderer.Series._cashItem.AddIndicator(ind);
            }
            void ind.Refresh();
            ind.MainChart = this;
            ind.TimeFrameInfo = this.TimeFrameInfo();
            ind.Symbol = this.Instrument().GetInteriorID();
            indRenderer.InvalidState = false;
        }
    }
    RemoveIndicator(indicatorRenderer, callCorrectIndicatorWindows = true) {
        if (indicatorRenderer != null) {
            const ind = indicatorRenderer.Indicator;
            this.mainPriceRenderer.Series._cashItem.RemoveIndicator(ind);
            if (indicatorRenderer.WindowsNumber < this.windowsContainer.Windows.length) {
                const renderer = this.windowsContainer.Windows[indicatorRenderer.WindowsNumber].GetRenderer(TerceraChartIndicatorStorageRenderer);
                const remIndex = renderer.Indicators.indexOf(indicatorRenderer);
                renderer.Indicators.splice(remIndex, 1);
            }
            indicatorRenderer.Dispose();
            if (callCorrectIndicatorWindows) {
                this.CorrectIndicatorWindows();
            }
            ind.Dispose();
        }
        this.OnRemoveIndicator.Raise(indicatorRenderer);
    }
    RemoveAllIndicators() {
        const allIndicatorRenderers = this.IndicatorRenderers();
        for (let i = 0; i < allIndicatorRenderers.length; i++) {
            this.RemoveIndicator(allIndicatorRenderers[i], true);
        }
        this.CorrectIndicatorWindows();
        this.IsDirty();
    }
    IndicatorRenderers() {
        if (this.windowsContainer == null) {
            return null;
        }
        const res = [];
        for (let i = 0; i < this.windowsContainer.Windows.length; i++) {
            const renderer = this.windowsContainer.Windows[i].GetRenderer(TerceraChartIndicatorStorageRenderer);
            const inds = renderer.Indicators;
            const l_ = inds.length;
            for (let j = 0; j < l_; j++) {
                res.push(inds[j]);
            }
        }
        return res;
    }
    CheckBarsForIndicator() {
        const count = this.BarsCount();
        const minBarsForInd = this.getMinBarsForInd();
        if (minBarsForInd > count) {
            this.autoLoadManager.setBarsToLoad(Math.round(minBarsForInd - count));
            this.autoLoadManager.update();
        }
    }
    CorrectIndicatorWindows() {
        let winCount = this.windowsContainer.Windows.length;
        for (let i = 0; i < winCount; i++) {
            const renderer = this.windowsContainer.Windows[i].GetRenderer(TerceraChartIndicatorStorageRenderer);
            for (let j = 0; j < renderer.Indicators.length; j++) {
                // Нашли индюк не на своем месте
                const indRenderer = renderer.Indicators[j];
                if (indRenderer != null && indRenderer.WindowsNumber != i) {
                    const remIndex = renderer.Indicators.indexOf(indRenderer);
                    renderer.Indicators.splice(remIndex, 1);
                    // Есть окно с подходящим номером
                    if (indRenderer.WindowsNumber < this.windowsContainer.Windows.length) {
                        const childRenderer = this.windowsContainer.Windows[indRenderer.WindowsNumber].GetRenderer(TerceraChartIndicatorStorageRenderer);
                        childRenderer.Indicators.push(indRenderer);
                    }
                    else {
                        // Создаем нужное количество окон
                        while (indRenderer.WindowsNumber >= this.windowsContainer.Windows.length) {
                            this.AddWindow();
                        }
                        const childRenderer = this.windowsContainer.Windows[indRenderer.WindowsNumber].GetRenderer(TerceraChartIndicatorStorageRenderer);
                        childRenderer.Indicators.push(indRenderer);
                    }
                }
            }
        }
        // Удаляем пустые окна
        winCount = this.windowsContainer.Windows.length;
        const windowsToRemove = [];
        for (let i = 1; i < winCount; i++) {
            const indRenderer = this.windowsContainer.Windows[i].GetRenderer(TerceraChartIndicatorStorageRenderer)
                .Indicators[0];
            if ((indRenderer === null || indRenderer === void 0 ? void 0 : indRenderer.Indicator) == null) {
                windowsToRemove.push(this.windowsContainer.Windows[i]);
            }
        }
        for (let i = 0; i < windowsToRemove.length; i++) {
            this.RemoveWindow(windowsToRemove[i]);
        }
        // Корректировка индексов (http://tp.pfsoft.net/entity/38104)
        for (let i = 1; i < this.windowsContainer.Windows.length; i++) {
            const renderer = this.windowsContainer.Windows[i].GetRenderer(TerceraChartIndicatorStorageRenderer);
            for (let j = 0; j < renderer.Indicators.length; j++) {
                const rend = renderer.Indicators[j];
                rend.WindowsNumber = i;
            }
        }
        this.CorrectPlateRenderers();
        this.LayoutWindowsContainers();
        this.IsDirty();
    }
    SaveIndicators() {
        var _a;
        const instrument = (_a = this.model) === null || _a === void 0 ? void 0 : _a.GetInstrument();
        const currentSymbol = instrument != null ? instrument.GetInteriorID() : '';
        let index = 0;
        const indicators = {};
        indicators.indicator = [];
        const allIndicatorRenderers = this.IndicatorRenderers();
        for (let i = 0; i < allIndicatorRenderers.length; i++) {
            const indicatorRenderer = allIndicatorRenderers[i];
            if (indicatorRenderer == null) {
                index++;
                continue;
            }
            const p = indicatorRenderer.Indicator;
            if (!currentSymbol.includes(p.Symbol)) {
                continue;
            }
            const indicatorDataToSave = {};
            if (!isNullOrUndefined(p.Script)) {
                indicatorDataToSave.ProjectName = p.Script.ProjectName;
            }
            const props = indicatorRenderer.Properties();
            const dp = DynProperty.getPropertyByName(props, 'Win num');
            if (dp != null) {
                dp.value = dp.value - index;
            }
            indicatorDataToSave.properties = DynProperty.serialize(props);
            indicatorDataToSave.LinesCount = p.LinesCount;
            indicatorDataToSave.SeparateWindow = p.SeparateWindow;
            indicators.indicator.push(indicatorDataToSave);
        }
        return indicators;
    }
    LoadIndicators(xml) {
        this.RemoveAllIndicators();
        if (isNullOrUndefined(xml)) {
            return;
        }
        const indicatorArray = xml.indicator;
        const len = indicatorArray.length;
        if (!Array.isArray(indicatorArray)) {
            try {
                this.LoadIndicator(indicatorArray);
            }
            catch (ex) {
                ErrorInformationStorage.GetException(ex);
            }
        }
        else {
            try {
                for (let i = 0; i < len; i++) {
                    this.LoadIndicator(indicatorArray[i]);
                }
            }
            catch (ex) {
                ErrorInformationStorage.GetException(ex);
            }
        }
    }
    LoadIndicator(xml) {
        const indicator = IndicatorManager.GetIndicator(xml.ProjectName);
        if (indicator === null)
            return;
        const indicatorProperties = DynProperty.deserialize(xml.properties);
        const indicatorRenderer = this.GetCharIndicatorRenderer(indicator, this);
        indicatorRenderer.callBack(indicatorProperties);
        /// /добавляю на чарт
        const renderer = this.mainWindow.GetRenderer(TerceraChartIndicatorStorageRenderer);
        renderer.Indicators.push(indicatorRenderer);
        this.CorrectIndicatorWindows();
        // #50999
        if (isNullOrUndefined(indicator.MainChart)) {
            indicator.MainChart = this;
        }
    }
    // #endregion
    // #region Overlays
    // Активный оверлей, если установлен для него рисуется шкала на главном окне
    ActiveOverlayGet() {
        return this.activeOverlay;
    }
    ActiveOverlaySet(value) {
        this.activeOverlay = value;
        if (!isNullOrUndefined(this.FocusedOverlay)) {
            this.FocusedOverlay = value;
        }
    }
    AddOverlayInstrument(instrumentName, callUpdateOverlays, style, historyType) {
        const overlay = new TerceraChartOverlay(this);
        overlay.Instrument = instrumentName;
        overlay.HistoryType = historyType;
        overlay.ChartDrawingTypeSet(style);
        this.overlayStorageRenderer.OverlaysWindows.push(overlay.window);
        this.Overlays.push(overlay);
        this.CorrectPlateRenderers();
        // Need for update OverlayWindow rectangle
        this.LayoutWindowsContainers();
        // Обновляем оверлеи
        if (callUpdateOverlays) {
            this.UpdateOverlay();
        }
        this.IsDirty();
        this.OnAddOverlay.Raise(overlay);
        return overlay;
    }
    SaveOverlays() {
        const overlayArr = [];
        const overlays = this.Overlays;
        const len = isValidArray(overlays) ? overlays.length : 0;
        for (let i = 0; i < len; i++) {
            const overlay = overlays[i];
            overlayArr.push({
                attributes: {
                    instrument: overlay.ActiveInstrument
                        ? overlay.ActiveInstrument.GetInteriorID()
                        : overlay.Instrument,
                    historyType: overlay.HistoryType,
                    style: overlay.ChartDrawingTypeGet(),
                    showOnChart: overlay.mainPriceRenderer.Visible ? 1 : 0,
                    showMarker: overlay.ShowMarker ? 1 : 0,
                    separateScale: overlay.SeparateScaleGet() ? 1 : 0,
                    scaleDataType: overlay.cashItemSeriesSettings.DataType
                },
                mainPriceRendererProperties: DynProperty.serialize(overlay.mainPriceRenderer.Properties())
            });
        }
        return { overlays: overlayArr };
    }
    LoadOverlaysAsync(xml) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isNullOrUndefined(xml)) {
                return;
            }
            try {
                this.RemoveAllOverlays();
                const overlaysJsonArr = xml.overlays;
                if (Array.isArray(overlaysJsonArr)) {
                    const len = overlaysJsonArr ? overlaysJsonArr.length : 0;
                    for (let i = 0; i < len; i++) {
                        yield this.LoadOverlayAsync(overlaysJsonArr[i]);
                    }
                }
                else {
                    yield this.LoadOverlayAsync(overlaysJsonArr);
                }
            }
            catch (ex) {
                ErrorInformationStorage.GetException(ex);
                console.log(ex);
            }
        });
    }
    LoadOverlayAsync(jsonItem) {
        return __awaiter(this, void 0, void 0, function* () {
            const attributes = jsonItem.attributes;
            const overlayInstrument = DataCache.getInstrumentByName(attributes.instrument);
            if (isNullOrUndefined(overlayInstrument)) {
                const searchHelper = new SearchHelper(attributes.instrument);
                yield DataCache.getInstrumentByInstrumentTradableID_NFL(searchHelper.InstrumentTradableID, searchHelper.Route, searchHelper.GetInteriorID());
            }
            const overlay = this.AddOverlayInstrument(attributes.instrument, false, parseInt(attributes.style), parseInt(attributes.historyType));
            if (isNullOrUndefined(overlay)) {
                return;
            }
            overlay.mainPriceRenderer.Visible = attributes.showOnChart == '1';
            overlay.ShowMarker = attributes.showMarker == '1';
            overlay.SeparateScaleSet(attributes.separateScale == '1');
            if (attributes.scaleDataType) {
                overlay.cashItemSeriesSettings.DataType = attributes.scaleDataType;
            }
            const properties = DynProperty.deserialize(jsonItem.mainPriceRendererProperties);
            overlay.mainPriceRenderer.callBack(properties);
        });
    }
    UpdateOverlay(mainChartTriggerAction = ChartLoadTrigger.RefreshAction) {
        try {
            if (DataCache == null || !DataCache.Loaded) {
                return;
            }
            setTimeout(() => {
                void this.UpdateOverlaysAsync(mainChartTriggerAction);
            }, 1);
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
        }
    }
    UpdateOverlaysAsync(mainChartTriggerAction) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                for (let i = 0; i < this.Overlays.length; i++) {
                    const overlay = this.Overlays[i];
                    yield this.LoadOverlayCashItem(overlay, mainChartTriggerAction);
                }
            }
            catch (ex) {
                ErrorInformationStorage.GetException(ex);
            }
        });
    }
    RemoveOverlay(over) {
        if (this.ActiveOverlayGet() === over) {
            this.ActiveOverlaySet(null);
        }
        const oldSeries = over.mainPriceRenderer.Series;
        oldSeries.HistoryExpanded.UnSubscribe(this.IsDirtyHist, this);
        ArrayUtils.RemoveElementFromArray(this.overlayStorageRenderer.OverlaysWindows, over.window);
        ArrayUtils.RemoveElementFromArray(this.Overlays, over);
        this.CorrectPlateRenderers();
        over.Dispose();
        this.layoutMainWindow();
        this.LayoutWindowsContainers();
        this.CheckBarsCountOnScreen();
        this.IsDirty();
        this.OnRemoveOverlay.Raise(over);
    }
    RemoveAllOverlays() {
        const overlays = this.Overlays;
        let i = overlays.length;
        while (i--)
            this.RemoveOverlay(overlays[i]);
    }
    // #endregion
    AddWindow() {
        const priceScaleRender = this.GetChartPriceScaleRenderer(this, this.yScaleRendererSettings, TerceraChartPriceScaleRendererWindowType.Indicator);
        const leftPriceScaleRender = this.GetChartPriceScaleRenderer(this, this.yScaleRendererSettings, TerceraChartPriceScaleRendererWindowType.Indicator);
        leftPriceScaleRender.Aligment = TerceraChartPriceScaleRendererAligment.Left;
        const ww = this.GetChartWindow(priceScaleRender, leftPriceScaleRender);
        ww.PointsConverter = new CashItemPointsCoverter(ww, this.mainPriceRenderer.Series);
        const toolInstanceRenderer = new TerceraChartToolInstanceRenderer(this.ChartID, this);
        toolInstanceRenderer.Instrument = this.Instrument();
        toolInstanceRenderer.InstanceWindow = ww;
        ww.Renderers.push(toolInstanceRenderer);
        ww.Renderers.splice(1, 0, this.mainWindow.GetMainThreadRenderer('TerceraChartNewToolRenderer'));
        ww.Renderers.push(new TerceraChartIndicatorStorageRenderer(this));
        this.CalculateNewWindowHeightKoef(ww);
        this.windowsContainer.Windows.push(ww);
        priceScaleRender.window = ww;
        leftPriceScaleRender.window = ww;
        const toolRenderer = ww.GetRenderer(TerceraChartToolRenderer);
        toolRenderer.SelectedToolsListChanged.Subscribe(this.ToolsRenderer_SelectedToolsListChanged, this);
        this.CorrectToolInstanceWindows();
        ww.ThemeChanged();
        return ww;
    }
    CalculateNewWindowHeightKoef(ww) {
        // normalize HeightKoef
        const normalizeKoef = 100 / this.mainWindow.HeightKoef;
        for (let i = 0; i < this.windowsContainer.Windows.length; i++) {
            this.windowsContainer.Windows[i].HeightKoef *= normalizeKoef;
        }
        // add first sub window
        if (this.windowsContainer.Windows.length === 1) {
            ww.HeightKoef = this.mainWindow.HeightKoef / 2;
        }
        // add second or third window
        if (this.windowsContainer.Windows.length >= 2) {
            let koefSum = 0;
            for (let i = 0; i < this.windowsContainer.Windows.length; i++) {
                if (!this.windowsContainer.Windows[i].IsMainWindow) {
                    koefSum += this.windowsContainer.Windows[i].HeightKoef;
                }
            }
            ww.HeightKoef = koefSum / (this.windowsContainer.Windows.length - 1);
            koefSum += ww.HeightKoef;
            // max height for sun windows
            let maxSubWindowHeight = 0;
            if (this.windowsContainer.Windows.length === 2) {
                maxSubWindowHeight = this.mainWindow.HeightKoef;
            }
            if (this.windowsContainer.Windows.length > 2) {
                maxSubWindowHeight = 2 * this.mainWindow.HeightKoef;
            }
            // recalculate all HeightKoef
            if (koefSum > maxSubWindowHeight) {
                for (let i = 0; i < this.windowsContainer.Windows.length; i++) {
                    if (!this.windowsContainer.Windows[i].IsMainWindow) {
                        this.windowsContainer.Windows[i].HeightKoef *= maxSubWindowHeight / koefSum;
                    }
                }
                ww.HeightKoef *= maxSubWindowHeight / koefSum;
            }
        }
    }
    RemoveWindow(ww) {
        const toolsRenderer = ww.GetRenderer(TerceraChartToolRenderer);
        toolsRenderer.SelectedToolsListChanged.UnSubscribe(this.ToolsRenderer_SelectedToolsListChanged, this);
        const remIndex = this.windowsContainer.Windows.indexOf(ww);
        this.windowsContainer.Windows.splice(remIndex, 1);
        ww.Dispose();
        this.CorrectToolInstanceWindows();
    }
    CorrectToolInstanceWindows() {
        const winCount = this.windowsContainer.Windows.length;
        for (let i = 0; i < winCount; i++) {
            const win = this.windowsContainer.Windows[i];
            const toolRenderer = win.GetRenderer(TerceraChartToolRenderer);
            if (toolRenderer instanceof TerceraChartToolInstanceRenderer) {
                toolRenderer.WindowsNumber = i;
                toolRenderer.RepopulateTools();
            }
        }
    }
    ToolsRenderer_SelectedToolsListChanged() {
        this.TerceraChartActionProcessor.StateChanged.Raise();
    }
    /// <summary>
    /// определяем для всех вложенных окон нужно ли место под рисование плашек индюков/оверлеев
    /// </summary>
    CorrectPlateRenderers() {
        //
        // определяем для всех вложенных окон нужно ли место под рисование плашек индюков/оверлеев
        //
        const winCount = this.windowsContainer.Windows.length;
        for (let i = 0; i < winCount; i++) {
            let plateRenderer = this.windowsContainer.Windows[i].GetMainThreadRenderer('TerceraChartPlateRenderer');
            if (i === 0) {
                const listOv = [];
                if (this.Overlays.length > 0) {
                    if (plateRenderer == null) {
                        plateRenderer = new TerceraChartPlateRenderer(this);
                        this.windowsContainer.Windows[i].Renderers.push(plateRenderer);
                    }
                    for (let j = 0; j < this.Overlays.length; j++) {
                        const overl = plateRenderer.GetPlateOverlay(this.Overlays[j], true);
                        listOv.push(overl);
                    }
                }
                if (plateRenderer != null) {
                    plateRenderer.UpdatePlateOverlays(listOv);
                }
            }
            const listInd = [];
            const renderer = this.windowsContainer.Windows[i].GetRenderer(TerceraChartIndicatorStorageRenderer);
            for (let j = 0; j < renderer.Indicators.length; j++) {
                try {
                    if (plateRenderer == null) {
                        plateRenderer = this.GetCharPlateIndicatorRenderer(this);
                        this.windowsContainer.Windows[i].Renderers.push(plateRenderer);
                    }
                    if (renderer.Indicators[j].Indicator != null) {
                        const indic = plateRenderer.GetPlateIndicator(renderer.Indicators[j], true);
                        listInd.push(indic);
                    }
                }
                catch (ex) {
                    ErrorInformationStorage.GetException(ex);
                }
            }
            if (plateRenderer != null) {
                plateRenderer.UpdatePlateIndicators(listInd);
                plateRenderer.WinNum = i;
            }
            const terceraChartWindow = this.windowsContainer.Windows[i];
            if ((plateRenderer != null && !plateRenderer.IsEmpty()) || terceraChartWindow.TerceraChartOverlayWindow) {
                this.windowsContainer.Windows[i].HeaderVisible = true;
                this.windowsContainer.Windows[i].PaddingTop = !this.windowsContainer.Windows[i].IsMainWindow
                    ? this.windowsContainer.Windows[i].HeaderHeight
                    : 0;
            }
            else {
                this.windowsContainer.Windows[i].PaddingTop = this.HeaderHeight;
                this.windowsContainer.Windows[i].HeaderVisible = false;
            }
        }
        // TODO MB SC2 Optimization ++13th
        this.IsDirty(LayersEnum.CrossHair);
        this.Draw();
    }
    getAllIndicatorRenderers() {
        const res = [];
        const winCount = this.windowsContainer.Windows.length;
        for (let i = 0; i < winCount; i++) {
            const indicatorsStorage = this.windowsContainer.Windows[i].GetRenderer(TerceraChartIndicatorStorageRenderer).Indicators;
            for (const element of indicatorsStorage) {
                res.push(element);
            }
        }
        return res;
    }
    /// <summary>
    /// Конструктор тулзовин
    /// </summary>
    CreateTool(toolType) {
        const tool = DataCacheToolFactory.Create(toolType, DataCache);
        tool.AssignedSymbol = this.Instrument().GetInteriorID();
        tool.Instrument = this.Instrument();
        return tool;
    }
    ProcessChangeTool(tool) {
        if (tool != null && this.Instrument() != null && tool.AssignedSymbol === this.Instrument().GetInteriorID()) {
            this.IsDirty(LayersEnum.Tools);
        }
    }
    // #region Tools
    // TODO. Optimize?
    get AllWindowsHoverAndSelectedTools() {
        let allTools = [];
        const wc = this.windowsContainer;
        if (!wc)
            return allTools;
        const windows = wc.Windows;
        if (windows) {
            const len = windows.length;
            for (let i = 0; i < len; i++) {
                const toolsRenderer = windows[i].GetRenderer(TerceraChartToolRenderer);
                if (!isNullOrUndefined(toolsRenderer)) {
                    allTools = allTools.concat(toolsRenderer.GetHoverAndSelectedToolsList());
                }
            }
        }
        return allTools;
    }
    // #endregion
    repopulate() {
        const allIndicators = this.IndicatorRenderers();
        const len = allIndicators.length;
        for (let i = 0; i < len; i++) {
            const curInd = allIndicators[i].Indicator;
            curInd.repopulate();
        }
        this.RefreshChart();
    }
    windowsContainer_AfterResizeWindows() {
        if (this.overlayStorageRenderer != null) {
            this.overlayStorageRenderer.LayoutWindows();
        }
    }
    /// <summary>
    /// Current order plasing type is OCO
    /// </summary>
    getIsOCOMode() {
        return this.TerceraChartTradingToolsRenderer.IsOCOMode;
    }
    setIsOCOMode(value) {
        this.TerceraChartTradingToolsRenderer.IsOCOMode = value;
    }
    Dispose() {
        var _a, _b;
        (_a = this.loadHistoryAbortController) === null || _a === void 0 ? void 0 : _a.abort();
        (_b = this.loadOverlayHistoryAbortController) === null || _b === void 0 ? void 0 : _b.abort();
        ToolsCache.OnChangeTool.UnSubscribe(this.ProcessChangeTool, this);
        const curSeries = this.mainPriceRenderer != null ? this.mainPriceRenderer.Series : null;
        if (curSeries !== null) {
            curSeries.HistoryExpanded.UnSubscribe(this.onMainSeriesHistoryExpanded, this);
            curSeries.Dispose();
        }
        this.Overlays.forEach((overlay) => {
            const overlayCashItem = overlay.mainPriceRenderer.Series;
            overlayCashItem === null || overlayCashItem === void 0 ? void 0 : overlayCashItem.HistoryExpanded.UnSubscribe(this.IsDirtyHist, this);
        });
        this.chartController.Dispose();
        this.autoLoadManager.loadingStateChanged.UnSubscribe(this._autoLoadingStateChanged, this);
        this.autoLoadManager.dispose();
        super.Dispose();
    }
    GetHistoryParams(ins = null, TimeFrameInfo = null, triggerAction = ChartLoadTrigger.ScrollingLeft, midDate) {
        var _a;
        const historyProps = {};
        historyProps.instrument = ins !== null && ins !== void 0 ? ins : this.model.GetInstrument();
        let account = this.model.GetAccount();
        if (account == null || !account.IsOwnedByUser) {
            account = DataCache.getPrimaryAccount();
        }
        historyProps.account = account;
        historyProps.TimeFrameInfo = TimeFrameInfo !== null && TimeFrameInfo !== void 0 ? TimeFrameInfo : this.TimeFrameInfo();
        /// если выбран тип истории default тут true, иначе НЕЛЬЗЯ менять тип, т.к. юзер задал свой любимый. юзать historyType
        const historyType = (_a = historyProps.TimeFrameInfo.HistoryType) !== null && _a !== void 0 ? _a : this.ChartHistoryType();
        historyProps.UseDefaultInstrumentHistoryType = historyType === ChartHistoryType.Default;
        switch (triggerAction) {
            case ChartLoadTrigger.RefreshAction: {
                historyProps.count = this.getInitBarsCount() * this.historyCoef;
                const now = new Date();
                historyProps.ToTime = new Date(now.getTime() + TimeSpan.FromMinutes(10).getTime());
                break;
            }
            case ChartLoadTrigger.LoadingRelativelyMidDate: {
                const count = this.getInitBarsCount();
                const addiditionalCount = count * (this.historyCoef - 1);
                const halfOfCount = Math.ceil(count / 2);
                historyProps.count = halfOfCount + addiditionalCount;
                historyProps.rightBarsCount = count - halfOfCount + addiditionalCount + 1; // Requesting one extra because two messages will have a duplicate middle bar. After removing the duplicate, the desired count remains.
                historyProps.FromTime = midDate;
                break;
            }
            case ChartLoadTrigger.ScrollingLeft: {
                const cashItemSeries = this.MainCashItemSeries;
                const propogatedLeftBorder = cashItemSeries.RealLeftRelativeBorderForPaddingBars !== 0
                    ? cashItemSeries.RealLeftRelativeBorderForPaddingBars
                    : cashItemSeries.LeftRelativeBorder;
                const leftDate = new Date(propogatedLeftBorder);
                // correct right date border, beacuse this border include existing bar
                const leftDateInMillis = leftDate.getTime();
                leftDate.setTime(leftDateInMillis - 1);
                historyProps.ToTime = leftDate;
                const initCount = this.getInitBarsCount();
                historyProps.count =
                    cashItemSeries.leftPaddingBarsCount + Math.ceil(initCount * this.historyCoef - this.mainWindow.i1);
                break;
            }
            case ChartLoadTrigger.ScrollingRight: {
                const cashItemSeries = this.MainCashItemSeries;
                const propogatedRightBorder = cashItemSeries.RealRightRelativeBorderForPaddingBars !== 0
                    ? cashItemSeries.RealRightRelativeBorderForPaddingBars
                    : cashItemSeries.RightRelativeBorder;
                const rightDate = new Date(propogatedRightBorder);
                const rightDateInMillis = rightDate.getTime();
                rightDate.setTime(rightDateInMillis + 1);
                historyProps.FromTime = rightDate;
                const initCount = this.getInitBarsCount();
                historyProps.count =
                    cashItemSeries.rightPaddingBarsCount +
                        Math.ceil(this.rightBarIndexIncludingEmptyBars + initCount * (this.historyCoef - 1) - this.BarsCount());
                historyProps.sortOrder = HistorySortOrder.Ascending;
                break;
            }
            case ChartLoadTrigger.LoadingPeriodChanged: {
                const cashItemSeries = this.MainCashItemSeries;
                historyProps.FromTime = new Date(cashItemSeries.LeftRelativeBorder);
                historyProps.ToTime = new Date(cashItemSeries.RightRelativeBorder);
                break;
            }
        }
        const historyParams = ReloadHistoryParams.createFrom(historyProps);
        return historyParams;
    }
    getInitBarsCount() {
        const minBarsForInd = this.getMinBarsForInd();
        return Math.max(Math.ceil(this.mainWindow.im()), minBarsForInd);
    }
    get DataExtenderAmount() {
        const series = this.MainCashItemSeries;
        if (isNullOrUndefined(series)) {
            return 0;
        }
        const list = series.FIndexList;
        return list.length > 0 ? list[0] : 0;
    }
    setPortfolioReturnsRendererProperty(key, value) {
        const portfolioRenderer = this.PortfolioChartReturnsRenderer;
        if (!portfolioRenderer)
            return;
        portfolioRenderer[key] = value;
    }
    // #endregion
    setDataForTradingCentralRenderer(data) {
        const tradingCentralRenderer = this.TradingCentralRenderer;
        if (!tradingCentralRenderer)
            return;
        tradingCentralRenderer.setLinesValues(data);
    }
    setAutoLoadHistory(value) {
        this.autoLoadManager.allowAutoLoad = value;
    }
    resetCurrentView() {
        this.autoLoadManager.resetCurrentView();
    }
    getActionByDataType(type) {
        switch (type) {
            case TerceraChartCashItemSeriesDataType.Absolute:
                return TerceraChartActionEnum.Absolute;
            case TerceraChartCashItemSeriesDataType.Log:
                return TerceraChartActionEnum.Log;
            case TerceraChartCashItemSeriesDataType.Relative:
                return TerceraChartActionEnum.Relative;
        }
    }
    // #region Hotkeys action
    ZoomIn() {
        this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.Zoom, ChartZoomRendererButtons.StepZoomIn));
        this.IsDirty();
    }
    ZoomOut() {
        this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.Zoom, ChartZoomRendererButtons.StepZoomOut));
        this.IsDirty();
    }
    AutoManualScaling() {
        const mainWindow = this.mainWindow;
        const scalingType = !mainWindow.AutoScale
            ? TerceraChartActionEnum.AutoScale
            : TerceraChartActionEnum.ManualScale;
        this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(scalingType, mainWindow));
        this.IsDirty();
    }
    MouseTrading() {
        this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.ChartTrading));
    }
    ShowHideInfoWindow() {
        const infoWindowRenderer = this.mainWindow.GetRenderer(TerceraChartInfoWindowRenderer);
        if (isNullOrUndefined(infoWindowRenderer)) {
            return;
        }
        const action = infoWindowRenderer.displayMode === InfoWindowDisplayMode.Hidden
            ? InfoWindowDisplayMode.SeparateWindow
            : InfoWindowDisplayMode.Hidden;
        this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.InfoWindowMode, action));
        this.IsDirty();
    }
    RemoveDrawings() {
        this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.DeleteAllTools));
    }
    ShowFilledOrders() {
        this.TerceraChartTradingToolsRenderer.ShowEvents = !this.TerceraChartTradingToolsRenderer.ShowEvents;
        this.IsDirty();
    }
    HotkeyDrawingProcess(toolType) {
        this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, toolType));
    }
    ShowHideGrid() {
        const gridTime = this.xScaleRendererSettings.ScaleGridVisibility;
        const gridPrice = this.yScaleRendererSettings.ScaleGridVisibility;
        if (gridTime === gridPrice) {
            this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.GridTime));
            this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.GridPrice));
        }
        else {
            if (gridTime) {
                this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.GridTime));
            }
            if (gridPrice) {
                this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.GridPrice));
            }
        }
    }
    ShowOpenPositions() {
        this.TerceraChartTradingToolsRenderer.ShowPositions = !this.TerceraChartTradingToolsRenderer.ShowPositions;
        this.IsDirty();
    }
    ShowVolume() {
        if (Resources.isHidden('property.hotkeyManager.ShowVolume')) {
            return;
        }
        this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.Volume));
        this.IsDirty();
    }
    ShowWorkingOrders() {
        this.TerceraChartTradingToolsRenderer.ShowOrders = !this.TerceraChartTradingToolsRenderer.ShowOrders;
        this.IsDirty();
    }
    // #endregion
    updateRightIndexByDownloadedBars(downloadedBars) {
        this.rightBarIndexIncludingEmptyBars += downloadedBars;
        this.lastMouseDownI1 += downloadedBars;
    }
    setIsChartHasMinSize(hasMinSize) {
        this.windowsContainer.isChartHasMinSize = hasMinSize;
    }
    isNeedUpdateRightIndex(newIndex) {
        return (this.mainWindow.i1 !== newIndex + this.mainPriceRenderer.Series.leftPaddingBarsCount ||
            super.isNeedUpdateRightIndex(newIndex));
    }
    getToBeginOffset() {
        const percentOfRightSpace = 10;
        const barsCount = this.getInitBarsCount();
        const emptyRightBars = (barsCount * percentOfRightSpace) / 100;
        return emptyRightBars;
    }
    goToNearestTime(goToDate) {
        const cashItem = this.MainCashItemSeries.CashItem;
        const nearestTime = new Date(cashItem.findNearestMatchingPeriodTime(goToDate));
        this.mainWindow.AutoScale = true;
        this.goToPositionBytime(nearestTime);
        this.goToTabletRenderer.setDate(nearestTime);
        this.IsDirty();
    }
    OnActionProcessor(action) { }
}
